const TOKEN_COOKIE_PREFIX = 'precept_auth_';
const COOKIE_ATTRIBUTES = {
  httpOnly: false, // Must be false to access from JS
  secure: process.env.NODE_ENV === 'production', // Only HTTPS in production
  sameSite: 'strict', // CSRF protection
  path: '/', // Available across the entire domain
  maxAge: 60 * 60 * 24 * 7, // 7 days in seconds
};


const setCookie = (name, value, attributes = {}) => {
  const finalAttributes = { ...COOKIE_ATTRIBUTES, ...attributes };

  let cookieString = `${name}=${encodeURIComponent(value)}; path=${finalAttributes.path}`;

  if (finalAttributes.maxAge) {
    cookieString += `; max-age=${finalAttributes.maxAge}`;
  }

  if (finalAttributes.expires) {
    cookieString += `; expires=${finalAttributes.expires}`;
  }

  if (finalAttributes.domain) {
    cookieString += `; domain=${finalAttributes.domain}`;
  }

  if (finalAttributes.secure) {
    cookieString += '; secure';
  }

  if (finalAttributes.sameSite) {
    cookieString += `; samesite=${finalAttributes.sameSite}`;
  }

  if (finalAttributes.httpOnly) {
    cookieString += '; httponly';
  }

  document.cookie = cookieString;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    const cookieValue = parts.pop().split(';').shift();
    return decodeURIComponent(cookieValue);
  }

  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

export const storeAuthTokens = (user) => {
  if (!user) return;

  // Store individual tokens as separate cookies
  if (user.idToken) {
    setCookie(`${TOKEN_COOKIE_PREFIX}id_token`, user.idToken);
  }

  if (user.accessToken) {
    setCookie(`${TOKEN_COOKIE_PREFIX}access_token`, user.accessToken);
  }

  if (user.refreshToken) {
    setCookie(`${TOKEN_COOKIE_PREFIX}refresh_token`, user.refreshToken);
  }

  // Store non-sensitive user data (excluding tokens) in localStorage for Redux
  const userDataForStorage = { ...user };
  delete userDataForStorage.idToken;
  delete userDataForStorage.accessToken;
  delete userDataForStorage.refreshToken;

  // Keep localStorage for non-sensitive data (needed for Redux state)
  // Note: localStorage changes will trigger the "storage" event for cross-tab sync
  localStorage.setItem("user", JSON.stringify(userDataForStorage));
};


export const getAuthTokens = () => {
  try {
    // Get non-sensitive user data from localStorage
    const storedUserData = localStorage.getItem("user");
    const userData = storedUserData ? JSON.parse(storedUserData) : {};

    // Get tokens from cookies
    const idToken = getCookie(`${TOKEN_COOKIE_PREFIX}id_token`);
    const accessToken = getCookie(`${TOKEN_COOKIE_PREFIX}access_token`);
    const refreshToken = getCookie(`${TOKEN_COOKIE_PREFIX}refresh_token`);

    // Return complete user object
    return {
      ...userData,
      ...(idToken && { idToken }),
      ...(accessToken && { accessToken }),
      ...(refreshToken && { refreshToken }),
    };
  } catch (error) {
    console.error("Error retrieving auth tokens:", error);
    return null;
  }
};


export const clearAuthTokens = () => {
  // Clear cookies
  deleteCookie(`${TOKEN_COOKIE_PREFIX}id_token`);
  deleteCookie(`${TOKEN_COOKIE_PREFIX}access_token`);
  deleteCookie(`${TOKEN_COOKIE_PREFIX}refresh_token`);

  // Clear localStorage
  // Note: localStorage changes will trigger the "storage" event for cross-tab sync
  localStorage.removeItem("user");
};


export const isAuthenticated = () => {
  const tokens = getAuthTokens();
  return !!(tokens?.idToken && tokens?.accessToken);
};
